/**
 * Authentication state manager (singleton)
 * Manages JWT tokens with localStorage persistence and cross-tab sync
 */

import type { AuthTokens, StateListener, UnsubscribeFunction } from '../types';
import { getRaw, setRaw, removeItem } from '../utils/storage';

export class AuthStore {
  private static instance: AuthStore;
  private tokens: AuthTokens | null = null;
  private listeners: Set<StateListener<AuthTokens | null>> = new Set();

  private constructor() {
    this.loadFromStorage();
    this.setupCrossTabSync();
  }

  static getInstance(): AuthStore {
    if (!AuthStore.instance) {
      AuthStore.instance = new AuthStore();
    }
    return AuthStore.instance;
  }

  getTokens(): AuthTokens | null {
    return this.tokens ? { ...this.tokens } : null;
  }

  getIdToken(): string | null {
    return this.tokens?.idToken || null;
  }

  getAccessToken(): string | null {
    return this.tokens?.accessToken || null;
  }

  getRefreshToken(): string | null {
    return this.tokens?.refreshToken || null;
  }

  setTokens(tokens: Partial<AuthTokens>): void {
    if (!this.tokens) {
      this.tokens = {};
    }

    if (tokens.idToken) {
      this.tokens.idToken = tokens.idToken;
      setRaw('idToken', tokens.idToken);
    }

    if (tokens.accessToken) {
      this.tokens.accessToken = tokens.accessToken;
      setRaw('accessToken', tokens.accessToken);
    }

    if (tokens.refreshToken) {
      this.tokens.refreshToken = tokens.refreshToken;
      setRaw('refreshToken', tokens.refreshToken);
    }

    this.notifyListeners();
  }

  clearTokens(): void {
    this.tokens = null;
    removeItem('idToken');
    removeItem('accessToken');
    removeItem('refreshToken');
    this.notifyListeners();
  }

  isAuthenticated(): boolean {
    return !!this.tokens?.idToken;
  }

  subscribe(listener: StateListener<AuthTokens | null>): UnsubscribeFunction {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  }

  loadFromStorage(): void {
    const idToken = getRaw('idToken');
    const accessToken = getRaw('accessToken');
    const refreshToken = getRaw('refreshToken');

    if (idToken || accessToken || refreshToken) {
      this.tokens = {
        idToken: idToken || undefined,
        accessToken: accessToken || undefined,
        refreshToken: refreshToken || undefined,
      };
    }
  }

  private setupCrossTabSync(): void {
    window.addEventListener('storage', (e) => {
      if (e.key === 'idToken' || e.key === 'accessToken' || e.key === 'refreshToken') {
        this.loadFromStorage();
        this.notifyListeners();
      }
    });
  }

  private notifyListeners(): void {
    const tokens = this.getTokens();
    this.listeners.forEach((listener) => listener(tokens));
  }
}
